import React from 'react';
import { Button, Stack } from '@mui/material';
import { useIntl } from 'react-intl';

import { useAppSelector } from '../../../store/hooks';
import { Appointment, Service } from '../../../types';
import { SeriesEditTimeline } from './SeriesEditTimeline';

import styles from './SeriesMemberEditBody.module.scss';

type SeriesMemberEditBodyProps = {
  appointment: Appointment;
  allServices: Service[];
  isPastEvent?: boolean;
  onRetrySeries: () => void;
  onToggleEdit?: (eventId: string) => void;
  onRecordOutcome?: (eventId: string) => void;
  onUndo?: (eventId: string) => void;
  undoingEventId?: string | null;
  onBookFollowUp?: () => void;
  onCopyToNew?: () => void;
  isCopyDisabled?: boolean;
  editingEventId: string | null;
  inlineEditContent?: React.ReactNode;
};

function SeriesMemberEditBodyComponent({
  onRetrySeries,
  onToggleEdit,
  onRecordOutcome,
  onUndo,
  undoingEventId,
  onBookFollowUp,
  onCopyToNew,
  isCopyDisabled,
  editingEventId,
  inlineEditContent,
}: SeriesMemberEditBodyProps) {
  const intl = useIntl();
  const isEditingAny = editingEventId !== null;

  const seriesState = useAppSelector((state) => state.series.getSeriesById);
  const isSeriesLoaded = !seriesState.loading && seriesState.data !== null && seriesState.error === null;

  const showCopy = Boolean(onCopyToNew) && !isEditingAny && isSeriesLoaded;

  return (
    <div className={styles.wrapper}>
      <SeriesEditTimeline
        editingEventId={editingEventId}
        onRetry={onRetrySeries}
        onToggleEdit={onToggleEdit}
        onRecordOutcome={onRecordOutcome}
        onUndo={onUndo}
        undoingEventId={undoingEventId}
        onBookFollowUp={onBookFollowUp}
        inlineEditContent={inlineEditContent}
      />

      {showCopy && (
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <Button data-testid="copy-to-new-button" variant="outlined" onClick={onCopyToNew} disabled={isCopyDisabled}>
            {intl.formatMessage({ id: 'appointment.copyToNew' })}
          </Button>
        </Stack>
      )}
    </div>
  );
}

SeriesMemberEditBodyComponent.defaultProps = {
  isPastEvent: false,
  onCopyToNew: undefined,
  isCopyDisabled: false,
  onUndo: undefined,
  undoingEventId: null,
};

export const SeriesMemberEditBody = React.memo(SeriesMemberEditBodyComponent);
